import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { pushNotificationService } from '../services/pushNotificationService';

const PushNotificationTester = () => {
  const [deviceToken, setDeviceToken] = useState('');
  const [deviceType, setDeviceType] = useState(Capacitor.getPlatform());
  const [isInitialized, setIsInitialized] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const initPushNotifications = async () => {
      try {
        await pushNotificationService.initialize();
        setIsInitialized(true);
        
        // Add listeners
        pushNotificationService.addListeners({
          onRegistration: (token) => {
            console.log('Device registered with token:', token);
            setDeviceToken(token);
          },
          onPushReceived: (notification) => {
            console.log('Push received:', notification);
          }
        });
      } catch (error) {
        console.error('Error initializing push notifications:', error);
        setError('Failed to initialize push notifications');
      }
    };

    initPushNotifications();

    // Cleanup
    return () => {
      pushNotificationService.removeAllListeners();
    };
  }, []);

  const handleTestLocalNotification = () => {
    // This is just a simulated notification for testing UI
    const notification = {
      title: 'Test Local Notification',
      body: 'This is a test notification (local)',
      id: Date.now().toString(),
      schedule: { at: new Date(Date.now() + 3000) }
    };

    // Display the notification in the UI
    setTestResult({
      type: 'local',
      notification,
      timestamp: new Date().toISOString()
    });
  };

  const handleTestServerNotification = async () => {
    setIsLoading(true);
    setError('');
    setTestResult(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/test_push_notification.php?dev_mode=1`);
      const data = await response.json();
      
      if (data.success) {
        setTestResult({
          type: 'server',
          response: data,
          timestamp: new Date().toISOString()
        });
      } else {
        // Build detailed error message with debug info
        let errorMessage = data.message || 'Failed to send test notification';
        
        if (data.errors && data.errors.length > 0) {
          errorMessage += '\n\nErrors:\n' + data.errors.join('\n');
        }
        
        if (data.debug) {
          errorMessage += '\n\nDebug Information:';
          if (typeof data.debug === 'object') {
            Object.keys(data.debug).forEach(key => {
              if (Array.isArray(data.debug[key])) {
                errorMessage += `\n${key}:\n  - ` + data.debug[key].join('\n  - ');
              } else {
                errorMessage += `\n${key}: ${JSON.stringify(data.debug[key])}`;
              }
            });
          } else {
            errorMessage += '\n' + JSON.stringify(data.debug, null, 2);
          }
        }
        
        if (data.fcm_response_android || data.fcm_response_ios) {
          errorMessage += '\n\nFCM Response:';
          if (data.fcm_response_android) {
            errorMessage += '\nAndroid: ' + JSON.stringify(data.fcm_response_android, null, 2);
          }
          if (data.fcm_response_ios) {
            errorMessage += '\niOS: ' + JSON.stringify(data.fcm_response_ios, null, 2);
          }
        }
        
        setError(errorMessage);
        setTestResult({
          type: 'server',
          response: data,
          timestamp: new Date().toISOString(),
          isError: true
        });
      }
    } catch (error) {
      console.error('Error testing server notification:', error);
      let errorMessage = 'Network error: Failed to connect to the server';
      if (error.message) {
        errorMessage += '\n\nError details: ' + error.message;
      }
      if (error.stack) {
        errorMessage += '\n\nStack trace:\n' + error.stack;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!Capacitor.isNativePlatform()) {
    return (
      <div className="push-notification-tester">
        <h2>Push Notification Tester</h2>
        <div className="alert alert-warning">
          Push notifications are only available on native platforms (iOS/Android).
          This component won't work in a web browser.
        </div>
      </div>
    );
  }

  return (
    <div className="push-notification-tester">
      <h2>Push Notification Tester</h2>
      
      <div className="status-section">
        <h3>Status</h3>
        <p>
          <strong>Initialized:</strong> {isInitialized ? 'Yes' : 'No'}
        </p>
        <p>
          <strong>Device Type:</strong> {deviceType}
        </p>
        <p>
          <strong>Device Token:</strong> 
          <span className="token">{deviceToken || 'Not registered yet'}</span>
        </p>
      </div>

      {error && (
        <div className="error-message" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px', maxHeight: '400px', overflow: 'auto', padding: '10px', backgroundColor: '#f8f8f8', border: '1px solid #ddd', borderRadius: '4px' }}>
          <strong>Error Details:</strong>
          <pre style={{ margin: '10px 0', whiteSpace: 'pre-wrap' }}>{error}</pre>
        </div>
      )}

      {testResult && (
        <div className={`test-result ${testResult.isError ? 'error-result' : ''}`}>
          <h3>{testResult.isError ? 'Test Result (Failed)' : 'Test Result'}</h3>
          <p><strong>Type:</strong> {testResult.type}</p>
          <p><strong>Time:</strong> {testResult.timestamp}</p>
          
          {testResult.type === 'server' && (
            <div>
              <p><strong>Success:</strong> {testResult.response.success ? 'Yes' : 'No'}</p>
              <p><strong>Message:</strong> {testResult.response.message}</p>
              {testResult.response.stats && (
                <div>
                  <p><strong>Total Tokens:</strong> {testResult.response.stats.total_tokens}</p>
                  <p><strong>Success Count:</strong> {testResult.response.stats.success}</p>
                  <p><strong>Android Tokens:</strong> {testResult.response.stats.android_tokens}</p>
                  <p><strong>iOS Tokens:</strong> {testResult.response.stats.ios_tokens}</p>
                </div>
              )}
              
              {testResult.response.debug && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                  <strong>Debug Information:</strong>
                  <pre style={{ margin: '10px 0', whiteSpace: 'pre-wrap', fontSize: '11px', maxHeight: '300px', overflow: 'auto' }}>
                    {JSON.stringify(testResult.response.debug, null, 2)}
                  </pre>
                </div>
              )}
              
              {testResult.response.errors && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '4px' }}>
                  <strong>Errors:</strong>
                  <ul>
                    {testResult.response.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {(testResult.response.fcm_response_android || testResult.response.fcm_response_ios) && (
                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e6f3ff', borderRadius: '4px' }}>
                  <strong>FCM Response:</strong>
                  <pre style={{ margin: '10px 0', whiteSpace: 'pre-wrap', fontSize: '11px', maxHeight: '300px', overflow: 'auto' }}>
                    {JSON.stringify({
                      android: testResult.response.fcm_response_android,
                      ios: testResult.response.fcm_response_ios
                    }, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="button-group">
        <button 
          onClick={handleTestLocalNotification}
          disabled={!isInitialized || isLoading}
          className="test-button"
        >
          Test Local Notification
        </button>
        
        <button 
          onClick={handleTestServerNotification}
          disabled={!isInitialized || isLoading || !deviceToken}
          className="test-button"
        >
          {isLoading ? 'Sending...' : 'Test Server Notification'}
        </button>
      </div>

      <div className="info-section">
        <h3>How to Test</h3>
        <ol>
          <li>Make sure you're running on a physical device (not an emulator/simulator)</li>
          <li>Wait for the device to register and get a token</li>
          <li>Click "Test Local Notification" to test local notifications</li>
          <li>Click "Test Server Notification" to test push notifications from the server</li>
        </ol>
        <p><strong>Note:</strong> Push notifications won't work in development mode on web browsers.</p>
      </div>
    </div>
  );
};

export default PushNotificationTester; 